import type { Application } from 'pixi.js';
import { resolutionFpsThresholds } from './display';

/**
 * 해상도 자동 조절 — 판이 버벅일 때만 그리는 해상도를 한 단계 낮춘다(판정·움직임과는 무관, 그리기만).
 *
 * "눈에 띄는 차이가 없어야 한다"가 조건이라 내릴 수 있는 경우를 좁혔다:
 *   - PC·태블릿(카메라 1배): 화면 배율이 정확히 2 이상인 모니터(레티나·4K 200%)에서만 2 → 1.
 *     도트 한 칸이 정확히 2×2 로 커질 뿐이라(캔버스는 pixelated 로 늘린다) 그림이 한 픽셀도 달라지지 않는다.
 *     배율 1 모니터는 원래 1이라 할 게 없고, 1.25·1.5 같은 배율은 늘리면 칸 크기가 들쭉날쭉해져서 안 한다.
 *   - 폰(카메라를 물린 화면): 2 → 1. 폰은 원래 화면보다 작게 그려 늘려 보이므로 차이가 거의 없다.
 * 내렸는데 빨라지지 않으면(그리기가 아니라 계산이 느린 기기) 바로 되돌리고 그 판에서는 다시 안 내린다.
 * 내린 뒤 오래 넉넉하면 한 번 되올려 보고, 곧 다시 버벅이면 그 판은 낮은 채로 둔다.
 */

/** 한 번 재는 길이(초) */
const WINDOW = 3;
/** 낮춘 뒤 이만큼 나아지지 않으면 효과 없다고 보고 되돌린다 */
const MIN_GAIN = 1.08;

type Phase = 'full' | 'trial' | 'low' | 'retry';

export interface AutoResState {
  full: number;
  low: number;
  current: number;
  phase: Phase;
  locked: 'none' | 'full' | 'low';
  lastFps: number;
}

export function attachAutoResolution(app: Application, isPlaying: () => boolean, isCompactView: () => boolean, getFrameLimit: () => number = () => 0): AutoResState {
  const full = app.renderer.resolution;
  const state: AutoResState = { full, low: full, current: full, phase: 'full', locked: 'none', lastFps: 0 };

  /** 눈에 띄는 차이 없이 내릴 수 있는 해상도. 없으면 full 그대로 */
  const lowFor = (): number => {
    if (isCompactView()) return full >= 1.5 ? 1 : full; // 폰: 2 → 1
    return full >= 2 && Number.isInteger(full) ? full / 2 : full; // PC: 정확히 2배일 때만
  };

  const apply = (res: number): void => {
    if (res === state.current) return;
    state.current = res;
    // 낮춘 캔버스는 이웃 칸을 그대로 늘린다 — 흐려지지 않고 도트 그대로
    app.canvas.style.imageRendering = res < full ? 'pixelated' : '';
    app.renderer.resize(app.screen.width, app.screen.height, res);
  };

  let frames = 0;
  let time = 0;
  let baseline = 0; // 낮추기 직전의 초당 프레임
  let goodFor = 0; // 낮은 채로 넉넉했던 시간(초)
  let previousLimit = getFrameLimit();

  app.ticker.add(() => {
    const limit = getFrameLimit();
    if (limit !== previousLimit) {
      previousLimit = limit;
      frames = time = goodFor = baseline = 0;
      state.phase = 'full';
      state.locked = 'none';
      apply(full);
    }
    const { low: LOW_FPS, high: HIGH_FPS } = resolutionFpsThresholds(limit);
    if (!isPlaying() || document.hidden) {
      frames = 0;
      time = 0;
      return;
    }
    frames++;
    time += app.ticker.elapsedMS / 1000;
    if (time < WINDOW) return;
    const fps = frames / time;
    frames = 0;
    time = 0;
    state.lastFps = Math.round(fps);

    switch (state.phase) {
      case 'full': {
        if (state.locked === 'full' || fps >= LOW_FPS) break;
        const low = lowFor();
        if (low >= full) break; // 차이 없이 내릴 방법이 없는 화면
        state.low = low;
        baseline = fps;
        apply(low);
        state.phase = state.locked === 'low' ? 'low' : 'trial';
        break;
      }
      case 'trial':
        // 낮췄는데 안 빨라졌다 — 그리기가 아니라 계산이 느린 기기다. 되돌리고 이 판에서는 다시 안 내린다
        if (fps < baseline * MIN_GAIN) {
          apply(full);
          state.locked = 'full';
          state.phase = 'full';
        } else {
          state.phase = 'low';
          goodFor = 0;
        }
        break;
      case 'low':
        if (state.locked === 'low') break;
        goodFor = fps >= HIGH_FPS ? goodFor + WINDOW : 0;
        if (goodFor >= 20) {
          // 한동안 넉넉했다 — 원래 해상도로 되올려 본다
          apply(full);
          state.phase = 'retry';
        }
        break;
      case 'retry':
        if (fps < LOW_FPS) {
          // 되올리자마자 다시 버벅인다 — 이 판은 낮은 채로 둔다
          apply(state.low);
          state.locked = 'low';
          state.phase = 'low';
        } else {
          state.phase = 'full';
        }
        break;
    }
  });

  return state;
}
